<script setup lang="ts">
/**
 * ImplantPanel — Implant management panel
 * Shows slots 1-5 (hardwirings) and 6-10 (boosters) with filled/empty states.
 */
import { computed } from 'vue';
import { useFitStore } from '../../stores/fitStore';
import { iconUrl } from '../../composables/useApi';
import type { FitImplant } from '../../../shared/types';

const store = useFitStore();

const fit = computed(() => store.activeFit);
const implants = computed<FitImplant[]>(() => fit.value?.implants ?? []);

function implantForSlot(slot: number): FitImplant | undefined {
  return implants.value.find(i => i.slot === slot);
}

function removeImplant(imp: FitImplant): void {
  store.removeImplantFromFit(imp.id);
}

function handleDrop(e: DragEvent, _slot: number): void {
  e.preventDefault();
  const data = e.dataTransfer?.getData('text/plain');
  if (!data) return;
  try {
    const parsed = JSON.parse(data);
    if (parsed.typeID) {
      store.addImplantToFit(parsed.typeID, parsed.typeName || 'Implant');
    }
  } catch { /* ignore bad drag data */ }
}

function handleDragOver(e: DragEvent): void {
  e.preventDefault();
  if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
}

const slotGroups = [
  { label: 'Hardwirings', slots: [1, 2, 3, 4, 5] },
  { label: 'Boosters', slots: [6, 7, 8, 9, 10] },
];
</script>

<template>
  <div class="implant-panel">
    <div
      v-for="group in slotGroups"
      :key="group.label"
      class="implant-group"
    >
      <div class="group-header">{{ group.label }}</div>
      <div class="implant-slots">
        <div
          v-for="slot in group.slots"
          :key="slot"
          class="implant-slot"
          :class="{ filled: !!implantForSlot(slot) }"
          @drop="handleDrop($event, slot)"
          @dragover="handleDragOver"
        >
          <template v-if="implantForSlot(slot)">
            <img
              :src="iconUrl(implantForSlot(slot)!.typeID, 32)"
              class="implant-icon"
              :alt="implantForSlot(slot)!.typeName"
            />
            <span class="implant-name">{{ implantForSlot(slot)!.typeName }}</span>
            <button
              class="implant-remove"
              @click="removeImplant(implantForSlot(slot)!)"
              title="Remove implant"
            >&times;</button>
          </template>
          <template v-else>
            <span class="slot-num">{{ slot }}</span>
            <span class="slot-empty">Empty</span>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.implant-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0;
}

.implant-group {
  display: flex;
  flex-direction: column;
}

.group-header {
  padding: 3px 8px;
  font-size: 0.68rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.implant-slots {
  display: flex;
  flex-direction: column;
}

.implant-slot {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  min-height: 28px;
  border-bottom: 1px solid rgba(42, 63, 95, 0.2);
  transition: background var(--transition-fast);
}

.implant-slot:hover {
  background: var(--bg-hover);
}

.implant-slot:not(.filled) {
  opacity: 0.5;
}

.implant-icon {
  width: 22px;
  height: 22px;
  border-radius: 2px;
  flex-shrink: 0;
}

.implant-name {
  font-size: 0.75rem;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.slot-num {
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  color: var(--text-secondary);
  border: 1px dashed var(--border-color);
  border-radius: 2px;
  flex-shrink: 0;
}

.slot-empty {
  font-size: 0.72rem;
  color: var(--text-secondary);
}

.implant-remove {
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 1rem;
  cursor: pointer;
  padding: 0 4px;
  opacity: 0;
  transition: opacity var(--transition-fast), color var(--transition-fast);
}

.implant-slot:hover .implant-remove {
  opacity: 1;
}

.implant-remove:hover {
  color: var(--accent-red);
}
</style>
